import { useRef } from 'react';
import { styled } from '@mui/material/styles';
import { useQuery } from '@tanstack/react-query';
import { ComplianceDocument } from '@/lib/types';
import ResilientImage from '@/components/ResilientImage/ResilientImage';
import { useInView } from '@/hooks/useInView';
import {
  canRenderPdfThumbnail,
  complianceThumbnailQueryKey,
  renderPdfThumbnail,
  THUMBNAIL_RENDER_WIDTH,
} from './ComplianceThumbnailHelpers';

const ComplianceThumbnail = ({
  file,
  className,
  'aria-label': ariaLabel = 'Document preview',
  priority,
}: {
  file: ComplianceDocument;
  className?: string;
  'aria-label'?: string;
  // Starts ahead of queued thumbnails, e.g. in the search dropdown.
  priority?: boolean;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref);

  const { data } = useQuery({
    queryKey: complianceThumbnailQueryKey(file.id, file.updated_at),
    queryFn: ({ signal }) =>
      renderPdfThumbnail(
        file.preview_url as string,
        { width: THUMBNAIL_RENDER_WIDTH, priority },
        signal,
      ),
    enabled: inView && canRenderPdfThumbnail(file),
    staleTime: Infinity,
    retry: false,
  });

  return (
    <Root ref={ref} className={className}>
      <Thumbnail
        src={data ?? null}
        alt={file.name}
        aria-label={ariaLabel}
      />
    </Root>
  );
};

const Root = styled('div')({
  overflow: 'hidden',
});

const Thumbnail = styled(ResilientImage)({
  objectPosition: 'top',
});

export default ComplianceThumbnail;
